import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import _, { throttle } from "underscore";

import type { TextFieldProps } from "@braintrust/braintrust-ui-components";
import {
  ComboBoxMultiple,
  Loader,
  PaperWithVioletOptions,
  TextField,
  Typography,
} from "@braintrust/braintrust-ui-components";
import { SearchIcon } from "@braintrust/braintrust-ui-components/Icons";
import { Snackbar } from "@js/components/snackbar";
import { useEffectRef } from "@js/hooks/use-effect-ref";

import type { GetSkillsQueryParams } from "../../api";
import { useCreateSkillMutation, useGetSkillsQuery } from "../../api";
import { NotFoundText } from "../../fields/components";
import { StyledSkillsAutocomplete } from "../../fields/styled";
import { useGetSkillsByIds } from "../../hooks";

export type SkillsAutocompleteProps = {
  value: number[];
  onChange: (skills: number[]) => void;
  error?: string;
  placeholder?: string;
  sx?: TextFieldProps["sx"];
  hideSearchIcon?: boolean;
  creatable?: boolean;
};

const MIN_OPTIONS_TO_DISPLAY = 10;

// Sentinel option id representing "add the typed text as a new skill name" — never a real skill id.
const NEW_SKILL_OPTION_ID = -1;

export const SkillsAutocomplete = ({
  value,
  onChange,
  error,
  placeholder,
  sx,
  hideSearchIcon,
  creatable,
}: SkillsAutocompleteProps) => {
  const [open, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [createSkillError, setCreateSkillError] = useState<string>();
  const [createSkill, { isLoading: isCreatingSkill }] =
    useCreateSkillMutation();
  const [skillsQueryArg, setSkillsQueryArg] = useState<GetSkillsQueryParams>({
    search: "",
    page: 1,
    ordering: "name",
  });
  const listboxRef = useRef<HTMLElement>(null);

  const {
    data: skillsResponse,
    isLoading,
    isFetching,
  } = useGetSkillsQuery(skillsQueryArg, { skip: !open });

  const currentPage = skillsQueryArg.page || 1;
  const skillsOptionsToDisplayCount = currentPage * SETTINGS.SKILLS_PER_PAGE;

  const handleSearchChangeDebounced = useMemo(() => {
    return _.debounce((newValue: string) => {
      setSkillsQueryArg((prev) => {
        return {
          ...prev,
          search: newValue,
          page: 1,
        };
      });
    }, 250);
  }, [setSkillsQueryArg]);

  useEffect(() => {
    return () => handleSearchChangeDebounced.cancel();
  }, [handleSearchChangeDebounced]);

  const skillsData = useMemo(
    () => skillsResponse?.results ?? [],
    [skillsResponse],
  );

  const skillOptionsToDisplay = useMemo(() => {
    const skills = skillsResponse?.results ?? [];
    const skillIds = skills
      .map((skill) => skill.id)
      .slice(0, skillsOptionsToDisplayCount);

    return skillIds;
  }, [skillsResponse, skillsOptionsToDisplayCount]);

  const optionsWithValue = useMemo(() => {
    return _.uniq([...skillOptionsToDisplay, ...value]);
  }, [value, skillOptionsToDisplay]);

  const trimmedInputValue = inputValue.trim();

  const isSearchPending = skillsQueryArg.search !== trimmedInputValue;
  const isSkillsSearchInProgress = isLoading || isFetching || isSearchPending;
  const canCreateSkill = useMemo(() => {
    if (
      !creatable ||
      !trimmedInputValue ||
      isSkillsSearchInProgress ||
      isCreatingSkill
    ) {
      return false;
    }

    const matchesExistingSkill = skillsData.some(
      (skill) => skill.name.toLowerCase() === trimmedInputValue.toLowerCase(),
    );
    return !matchesExistingSkill;
  }, [
    creatable,
    trimmedInputValue,
    skillsData,
    isSkillsSearchInProgress,
    isCreatingSkill,
  ]);

  const { data: selectedSkills, isFetching: isFetchingSelectedSkills } =
    useGetSkillsByIds(
      { ids: value },
      { skip: !open || !trimmedInputValue || !canCreateSkill },
    );

  const showAddSkillOption = useMemo(() => {
    if (isFetchingSelectedSkills || !selectedSkills) {
      return false;
    }

    const matchesSelectedSkill = selectedSkills.some(
      (skill) => skill.name.toLowerCase() === trimmedInputValue.toLowerCase(),
    );
    return canCreateSkill && !matchesSelectedSkill;
  }, [
    canCreateSkill,
    selectedSkills,
    trimmedInputValue,
    isFetchingSelectedSkills,
  ]);

  const displayedOptions = useMemo(() => {
    if (!showAddSkillOption) {
      return optionsWithValue;
    }
    return [NEW_SKILL_OPTION_ID, ...optionsWithValue];
  }, [optionsWithValue, showAddSkillOption]);

  const hasRenderableOptions =
    skillOptionsToDisplay.length > 0 || showAddSkillOption;

  // We're hiding the options if there are no options to show and search is in progress to hide that 'Loading...' ...
  // ... bar after the user starts typing. We already show the loader
  const isSkillSearchOrCratableCheckInProgress =
    isSkillsSearchInProgress || isFetchingSelectedSkills;
  const showOptions =
    hasRenderableOptions || !isSkillSearchOrCratableCheckInProgress;

  const fetchNextPage = useCallback(() => {
    setSkillsQueryArg((prev) => {
      const prevPage = prev.page || 1;
      return { ...prev, page: prevPage + 1 };
    });
  }, []);

  const hasNotEnoughOptionsToDisplay = useMemo(() => {
    if (isFetching) {
      return false;
    }

    const displayedOptionsCount = optionsWithValue.length - value.length;
    return displayedOptionsCount < MIN_OPTIONS_TO_DISPLAY;
  }, [optionsWithValue, value, isFetching]);

  const hasNextPage = !!skillsResponse?.next;
  const shouldFillOptionsList =
    hasNotEnoughOptionsToDisplay && hasNextPage && open;

  const handleScroll = () => {
    if (shouldFillOptionsList) {
      // useEffect will trigger fetching next pages
      return;
    }

    if (!hasNextPage || isFetching || !listboxRef.current) {
      return;
    }
    const target = listboxRef.current;
    const listHeight = target.scrollHeight;
    const offsetBottom = target.scrollTop + target.offsetHeight;
    const shouldFetch = listHeight - offsetBottom < 200;
    if (!shouldFetch) {
      return;
    }

    fetchNextPage();
  };

  const handleScrollRef = useEffectRef(handleScroll);
  const throttledHandleScroll = useMemo(() => {
    return throttle(() => handleScrollRef.current(), 200);
  }, [handleScrollRef]);

  const handleInputChange = (_event: unknown, newInputValue: string) => {
    setInputValue(newInputValue);
    setCreateSkillError(undefined);
    handleSearchChangeDebounced(newInputValue);
  };

  useEffect(() => {
    if (!shouldFillOptionsList) {
      return;
    }
    fetchNextPage();
  }, [shouldFillOptionsList, fetchNextPage]);

  return (
    <ComboBoxMultiple<number, true>
      id="skillsCombobox"
      value={value}
      options={displayedOptions}
      onInputChange={handleInputChange}
      initialTaxonomiesLoading={isLoading}
      component={StyledSkillsAutocomplete}
      open={open && showOptions}
      onOpen={() => {
        setIsOpen(true);
      }}
      onClose={() => {
        setIsOpen(false);
      }}
      onChange={async (_ev, values) => {
        if (!values) {
          return;
        }
        const unique = _.uniq(values, false);
        if (!unique.includes(NEW_SKILL_OPTION_ID)) {
          onChange(unique);
          return;
        }

        const skillIds = unique.filter((id) => id !== NEW_SKILL_OPTION_ID);
        if (!trimmedInputValue) {
          onChange(skillIds);
          return;
        }

        setCreateSkillError(undefined);
        try {
          const created = await createSkill({
            name: trimmedInputValue,
          }).unwrap();

          if (!created.is_valid) {
            Snackbar.error(
              `"${created.name}" didn't pass validation and couldn't be added.`,
            );
            return;
          }

          onChange([...skillIds, created.id]);
        } catch {
          setCreateSkillError("Couldn't add this skill. Please try again.");
        }
      }}
      onKeyDownCapture={(ev) => {
        if (ev.key === "ArrowDown" && isFetching) {
          ev.stopPropagation();
        }
      }}
      onHighlightChange={(_ev, option) => {
        const optionsCount = skillOptionsToDisplay.length;
        const lastOption = skillOptionsToDisplay[optionsCount - 1];
        const isLastOptionHighligthed = lastOption === option;
        if (isLastOptionHighligthed) {
          handleScroll();
        }
      }}
      renderInput={(params) => {
        return (
          <TextField
            {...params}
            size="small"
            helperText={error ?? createSkillError}
            error={!!(error ?? createSkillError)}
            placeholder={placeholder ?? "Search skills"}
            sx={sx}
            slotProps={{
              ...params.slotProps,
              htmlInput: {
                ...params.slotProps.htmlInput,
                "aria-label": "Add Skills",
              },
              input: {
                ...params.slotProps.input,
                startAdornment: !hideSearchIcon && (
                  <SearchIcon sx={{ color: "var(--grey-1)" }} />
                ),
                endAdornment: (
                  <>
                    {isSkillSearchOrCratableCheckInProgress && (
                      <Loader size={20} />
                    )}
                    {params.slotProps.input.endAdornment}
                  </>
                ),
              },
            }}
          />
        );
      }}
      noOptionsText={<NotFoundText />}
      getOptionLabel={(option) => {
        if (option === NEW_SKILL_OPTION_ID) {
          return trimmedInputValue;
        }
        const optionData = skillsData?.find((skill) => skill.id === option);
        if (!optionData) {
          return String(option);
        }
        return optionData.name;
      }}
      groupBy={(option) => {
        if (option === NEW_SKILL_OPTION_ID) {
          return "";
        }
        const optionData = skillsData?.find((skill) => skill.id === option);
        if (!optionData) {
          return "";
        }

        const firstLetter = optionData.name
          .replace(/^(\.)/, "")
          .charAt(0)
          .toUpperCase();
        return /[0-9]/.test(firstLetter) ? "0-9" : firstLetter;
      }}
      renderOption={({ key, ...optionProps }, option) => {
        if (option === NEW_SKILL_OPTION_ID) {
          return (
            <li {...optionProps} key={key}>
              <Typography component="span">
                <Typography component="span" fontWeight={500}>
                  Add
                </Typography>
                {` "${trimmedInputValue}"`}
              </Typography>
            </li>
          );
        }

        const optionData = skillsData?.find((skill) => skill.id === option);
        if (!optionData) {
          return null;
        }

        return (
          <li {...optionProps} key={key}>
            {optionData.name}
          </li>
        );
      }}
      disableClearable
      disableValueRemoval
      filterSelectedOptions
      displayAllOptions
      slotProps={{
        listbox: {
          ref: listboxRef,
          onScroll: throttledHandleScroll,
        },
      }}
      slots={{ paper: PaperWithVioletOptions }}
    />
  );
};
